/**
 * Extracts a human-readable summary from an If action's expression.
 * Returns a truncated string like "status = \"Approved\"" or undefined if it can't parse.
 */
export function getConditionSummary(expression: unknown, maxLength = 40): string | undefined {
  if (!expression) return undefined;

  try {
    let expr: Record<string, unknown> | undefined;

    if (typeof expression === 'string') {
      if (expression.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(expression);
        if (parsed && typeof parsed === 'object') {
          expr = parsed as Record<string, unknown>;
        } else {
          return expression.length > maxLength
            ? expression.substring(0, maxLength - 1) + '…'
            : expression;
        }
      } catch {
        return expression.length > maxLength
          ? expression.substring(0, maxLength - 1) + '…'
          : expression;
      }
    } else if (typeof expression === 'object') {
      expr = expression as Record<string, unknown>;
    } else {
      return undefined;
    }

    if (!expr) return undefined;

    // Handle simple condition: { and: [{ equals: [a, b] }] } or { or: [...] }
    const operator = Object.keys(expr)[0]; // 'and', 'or', 'not'
    if (!operator) return undefined;

    const conditions = expr[operator];
    if (!Array.isArray(conditions) || conditions.length === 0) return undefined;

    // Summarize first condition
    const first = conditions[0];
    if (typeof first !== 'object' || first === null) return undefined;

    const compOp = Object.keys(first as Record<string, unknown>)[0];
    if (!compOp) return undefined;

    const operands = (first as Record<string, unknown>)[compOp];
    if (!Array.isArray(operands) || operands.length < 2) return undefined;

    const left = summarizeOperand(operands[0]);
    const right = summarizeOperand(operands[1]);
    const opLabel = getOperatorLabel(compOp);

    let summary = `${left} ${opLabel} ${right}`;

    if (conditions.length > 1) {
      summary += ` ${operator} ${conditions.length - 1} more`;
    }

    if (summary.length > maxLength) {
      return summary.substring(0, maxLength - 1) + '…';
    }

    return summary;
  } catch {
    return undefined;
  }
}

function summarizeOperand(operand: unknown): string {
  if (typeof operand === 'string') {
    // Expression like ${triggerOutputs()?.['body/status']} or legacy @triggerOutputs()?.['body/status']
    if (operand.startsWith('@') || operand.startsWith('${')) {
      const match = operand.match(/\['([^']+)'\]/);
      if (match?.[1]) return match[1].split('/').pop() ?? operand;
      const funcMatch = operand.match(/[@${]*(\w+)\(/);
      if (funcMatch?.[1]) return funcMatch[1];
      return operand;
    }
    return `"${operand}"`;
  }
  if (typeof operand === 'number' || typeof operand === 'boolean') {
    return String(operand);
  }
  return '…';
}

function getOperatorLabel(op: string): string {
  switch (op) {
    case 'equals': return '=';
    case 'not_equals':
    case 'notEquals': return '≠';
    case 'greater':
    case 'greaterThan': return '>';
    case 'greaterOrEquals':
    case 'greaterThanOrEquals': return '≥';
    case 'less':
    case 'lessThan': return '<';
    case 'lessOrEquals':
    case 'lessThanOrEquals': return '≤';
    case 'contains': return 'contains';
    case 'notContains': return 'does not contain';
    case 'startsWith': return 'starts with';
    case 'endsWith': return 'ends with';
    case 'isEmpty': return 'is empty';
    case 'isNotEmpty': return 'is not empty';
    default: return op;
  }
}
